/**
 * SuggestDrillsButton — visible in the Drill Library toolbar when a client is selected.
 *
 * Clicking it builds DrillSuggestionFilters from the client's latest posture/SoP data
 * and navigates to the Drill Planner with that payload in router state.
 *
 * @see docs/features/260331-automated-driller-mvp/KICKOFF_PROMPT.md §4
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wand2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClient } from '@/context/client-context';
import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import { buildClientPlannerFilters } from '@/lib/clients/needs-tags';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import logger from '@/lib/logging';

/** Navigation state payload carried to the Drill Planner route. */
export interface SuggestDrillsState {
  filters: DrillSuggestionFilters;
  clientId: string;
}

const SuggestDrillsButton: React.FC = () => {
  const { client } = useClient();
  const navigate = useNavigate();
  const [isBuilding, setIsBuilding] = useState(false);

  const handleClick = useCallback(async () => {
    if (!client) return;
    setIsBuilding(true);

    try {
      const filters = buildClientPlannerFilters(client);
      const state: SuggestDrillsState = { filters, clientId: client.id };

      logger.info('drill-plan:suggest-drills:navigate', {
        clientId: client.id,
        phaseTag: filters.phaseTag,
        limitationsCount: filters.movementLimitations.length,
      });

      navigate('/drill-planner?mode=create', { state });
    } catch (err) {
      logger.error('drill-plan:suggest-drills:error', err);
    } finally {
      setIsBuilding(false);
    }
  }, [client, navigate]);

  if (!client) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={isBuilding}
      className="h-10 w-10 p-0 sm:w-auto sm:px-3 sm:gap-1.5 flex-shrink-0"
      title={`Suggest drills for ${clientDisplayLabel(client)}`}
    >
      {isBuilding ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Wand2 className="h-4 w-4" />
      )}
      <span className="hidden sm:inline text-xs font-semibold whitespace-nowrap">
        Suggest Drills
      </span>
    </Button>
  );
};

export default SuggestDrillsButton;
